import { QueryClient } from '@tanstack/react-query'
import { DateTime } from 'luxon'

import { PlantStoppagesGroup, stoppagesAjax } from '../ajax/StoppagesAjax'
import { ErrorResponse } from '../DTOs/Common/ErrorResponse'
import { StoppageDto } from '../DTOs/Stoppage/StoppageDto'
import { TripleShift } from '../types/Shift'
import { PagedResult } from '../DTOs/Common/PagedResult'

/** A machine as the Operations list knows it: which plant, and the status its nested table is keyed on. */
export interface PlantStatusEntry {
  plantId: number
  status: string
}

export function plantStoppagesQueryKey(dateIso: string, shift: string, plantId: number, status: string | undefined): unknown[] {
  return ['dataTable', 'operations', 'plantStoppages', dateIso, shift, plantId, status, 1, 5, null, 'asc', false, null]
}

export function plantStoppagesCacheEntries(
  dateIso: string,
  shift: string,
  entries: PlantStatusEntry[],
  groups: PlantStoppagesGroup[],
): { key: unknown[], page: PagedResult<StoppageDto> }[] {
  const byPlant = new Map(groups.map(group => [group.plantId, group.stoppages]))

  return entries.map(entry => ({
    key: plantStoppagesQueryKey(dateIso, shift, entry.plantId, entry.status),
    page: byPlant.get(entry.plantId) ?? PagedResult.empty<StoppageDto>(1, 5),
  }))
}

export function plantStatusSignature(dateIso: string, shift: string, entries: PlantStatusEntry[]): string {
  return [dateIso, shift, ...entries.map(entry => `${entry.plantId}:${entry.status}`)].join('|')
}

export async function warmPlantStoppagesCache(
  queryClient: QueryClient,
  date: DateTime,
  shift: TripleShift,
  entries: PlantStatusEntry[],
): Promise<void> {
  const dateIso = date.toISODate()
  if (!dateIso || entries.length === 0) return

  let response: PagedResult<PlantStoppagesGroup> | ErrorResponse
  try {
    response = await stoppagesAjax.getStoppagesForShift(date, shift)
  } catch {
    return
  }
  if (ErrorResponse.isErrorResponse(response)) return
  const groups = response.data

  for (const { key, page } of plantStoppagesCacheEntries(dateIso, shift, entries, groups)) {
    queryClient.setQueryData(key, page)
  }
}
